import { ref } from "vue";
import { supabase } from "../lib/supabase";
import { useToast } from "./useToast";
import { useInspectiecertificaat } from "./useInspectiecertificaat";
import { useRapportPaymentHolds } from "./useRapportPaymentHolds";
import { getInspectieTypeParam, getRapportDetailsPath } from "../utils/rapportScope";
import { captureError } from "../lib/sentry";
import { inspectieTypeLabels, type InspectieStatus } from "../../../../types";

export type PortalDocumentKind = "certificaat" | "rapport" | "offerte" | "bundel" | "factuur" | "herstel";

export const portalDocumentKindLabels: Record<PortalDocumentKind, string> = {
  certificaat: "Certificaat",
  rapport: "Rapport",
  offerte: "Offerte",
  bundel: "Offertebundel",
  factuur: "Factuur",
  herstel: "Hersteldocument",
};

export interface PortalDocument {
  /** Unique key within the assembled list. */
  id: string;
  kind: PortalDocumentKind;
  /** Display name of the document, e.g. "Certificaat Scope 10". */
  label: string;
  /** Short name of the linked entity, e.g. "Rapport 24-0012". */
  entityLabel: string;
  /** Extra context for the linked entity (locatie naam/adres). */
  entityDescription: string | null;
  /** Router path to the linked entity. */
  entityPath: string;
  /** ISO date used for sorting/display. */
  date: string | null;
  /** Storage bucket the PDF lives in. */
  bucket: "generated-docs" | "userFiles";
  /** Storage path of the PDF within the bucket. */
  pdfPath: string;
  /** Filename used for the download. */
  fileName: string;
}

interface RapportEmbed {
  id: string;
  inspectieRapportNr: string | null;
  locatie_snapshot: { naam?: string | null; straat?: string | null; huisnr?: string | null; plaats?: string | null } | null;
}

const DOCUMENTS_SELECT = `
  id,
  type,
  status,
  pdf_path,
  created_at,
  updated_at,
  rapport:rapport_id ( id, inspectieRapportNr, locatie_snapshot ),
  offerte:offerte_id ( id, offerteNr, locatie:locatie_id ( naam, straat, huisnr, plaats ) ),
  bundel:bundel_id ( id, bundel_nr, naam ),
  factuur:factuur_id ( id, factuurnr )
`;

function formatLocatie(locatie: RapportEmbed["locatie_snapshot"]): string | null {
  if (!locatie) return null;
  const address = [locatie.straat, locatie.huisnr].filter(Boolean).join(" ");
  const parts = [locatie.naam, [address, locatie.plaats].filter(Boolean).join(", ")].filter(Boolean);
  return parts.length ? parts.join(" — ") : null;
}

function getScopeFromType(type: string): { scope: ReturnType<typeof getInspectieTypeParam>; scopeLabel: string } {
  const suffix = type.split("_")[1] || "";
  const scope = getInspectieTypeParam(suffix);
  const scopeLabel = scope ? inspectieTypeLabels[scope] || scope : suffix;
  return { scope, scopeLabel };
}

function buildRapportEntity(rapport: RapportEmbed, type: string) {
  const { scope, scopeLabel } = getScopeFromType(type);
  return {
    scopeLabel,
    entityLabel: `Rapport ${rapport.inspectieRapportNr || "zonder nummer"}`,
    entityDescription: formatLocatie(rapport.locatie_snapshot || null),
    entityPath: scope ? getRapportDetailsPath(rapport.id, scope) : `/rapport/${rapport.id}`,
    nr: rapport.inspectieRapportNr || rapport.id,
  };
}

function mapGeneratedDocument(row: any): PortalDocument | null {
  if (!row?.pdf_path) return null;

  const type = String(row.type || "");
  const date = row.updated_at || row.created_at || null;
  const base = {
    id: `doc-${row.id}`,
    date,
    bucket: "generated-docs" as const,
    pdfPath: String(row.pdf_path),
  };

  if (type.startsWith("inspectiecertificaat_") || type.startsWith("rapport") || type.startsWith("herstel")) {
    // Without the rapport embed (row filtered by RLS) we can neither label nor link the document.
    if (!row.rapport) return null;
    const entity = buildRapportEntity(row.rapport, type);
    let kind: PortalDocumentKind;
    let label: string;

    if (type.startsWith("inspectiecertificaat_")) {
      kind = "certificaat";
      label = `Certificaat ${entity.scopeLabel}`.trim();
    } else if (type.startsWith("herstelformulier")) {
      kind = "herstel";
      label = `Herstelformulier ${entity.scopeLabel}`.trim();
    } else if (type.startsWith("herstelrapport")) {
      kind = "herstel";
      label = `Herstelrapport ${entity.scopeLabel}`.trim();
    } else {
      kind = "rapport";
      label = `Inspectierapport ${entity.scopeLabel}`.trim();
    }

    return {
      ...base,
      kind,
      label,
      entityLabel: entity.entityLabel,
      entityDescription: entity.entityDescription,
      entityPath: entity.entityPath,
      fileName: `${label} - ${entity.nr}.pdf`,
    };
  }

  if (type === "offerte" && row.offerte) {
    const nr = row.offerte.offerteNr || row.offerte.id;
    return {
      ...base,
      kind: "offerte",
      label: `Offerte ${row.offerte.offerteNr || ""}`.trim(),
      entityLabel: `Offerte ${row.offerte.offerteNr || "zonder nummer"}`,
      entityDescription: formatLocatie(row.offerte.locatie || null),
      entityPath: `/offerte/${row.offerte.id}`,
      fileName: `Offerte ${nr}.pdf`,
    };
  }

  if (type === "offerte_bundel" && row.bundel) {
    const nr = row.bundel.bundel_nr || row.bundel.id;
    return {
      ...base,
      kind: "bundel",
      label: row.bundel.naam ? `Offertebundel ${row.bundel.naam}` : `Offertebundel ${nr}`,
      entityLabel: `Bundel ${nr}`,
      entityDescription: row.bundel.naam || null,
      entityPath: `/bundel/${row.bundel.id}`,
      fileName: `Offertebundel ${nr}.pdf`,
    };
  }

  if (type === "factuur" && row.factuur) {
    const nr = row.factuur.factuurnr || row.factuur.id;
    return {
      ...base,
      kind: "factuur",
      label: `Factuur ${row.factuur.factuurnr || ""}`.trim(),
      entityLabel: `Factuur ${row.factuur.factuurnr || "zonder nummer"}`,
      entityDescription: null,
      entityPath: `/factuur/${row.factuur.id}`,
      fileName: `Factuur ${nr}.pdf`,
    };
  }

  return null;
}

/** Parse the upload timestamp out of a SCIOS certificaat_pad ("...-<epoch ms>.pdf"). */
function parseSciosUploadDate(certificaatPad: string): string | null {
  const match = certificaatPad.match(/-(\d{12,14})\.pdf$/);
  if (!match) return null;
  const timestamp = Number(match[1]);
  if (!Number.isFinite(timestamp)) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function mapSciosCertificaten(rapport: any): PortalDocument[] {
  const entries: InspectieStatus[] = Array.isArray(rapport?.inspectieStatus) ? rapport.inspectieStatus : [];
  const documents: PortalDocument[] = [];

  for (const entry of entries) {
    if (!entry?.certificaat || !entry.certificaat_pad) continue;

    const scopeLabel = inspectieTypeLabels[entry.type] || entry.type;
    const nr = rapport.inspectieRapportNr || rapport.id;
    const eindDatum = entry.eindDatum ? String(entry.eindDatum) : null;

    documents.push({
      id: `scios-${rapport.id}-${entry.type}`,
      kind: "certificaat",
      label: `Certificaat ${scopeLabel}`,
      entityLabel: `Rapport ${rapport.inspectieRapportNr || "zonder nummer"}`,
      entityDescription: formatLocatie(rapport.locatie_snapshot || null),
      entityPath: getRapportDetailsPath(rapport.id, entry.type),
      date: parseSciosUploadDate(entry.certificaat_pad) || eindDatum || rapport.created_at || null,
      bucket: "userFiles",
      pdfPath: entry.certificaat_pad,
      fileName: `Certificaat ${scopeLabel} - ${nr}.pdf`,
    });
  }

  return documents;
}

/**
 * Aggregates all documents an opdrachtgever has access to:
 * - completed generated documents (offerte/bundel/factuur/rapport/herstel/
 *   inspectiecertificaat PDFs) from public.documents (RLS-scoped via
 *   can_select_document), and
 * - uploaded SCIOS certificaten referenced by rapporten.inspectieStatus[].certificaat_pad.
 */
export function useDocumenten() {
  const documenten = ref<PortalDocument[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const downloadingId = ref<string | null>(null);
  const toast = useToast();
  const { downloadSciosCertificaat } = useInspectiecertificaat();
  const { fetchRapportPaymentHolds } = useRapportPaymentHolds();

  async function fetchDocumenten(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const [documentsResult, rapportenResult] = await Promise.all([
        supabase
          .from("documents")
          .select(DOCUMENTS_SELECT)
          .eq("status", "completed")
          .not("pdf_path", "is", null)
          .order("updated_at", { ascending: false })
          .limit(500),
        supabase.from("rapporten").select("id, inspectieRapportNr, inspectieStatus, locatie_snapshot, created_at"),
      ]);

      if (documentsResult.error) throw documentsResult.error;
      if (rapportenResult.error) throw rapportenResult.error;

      const generated = (documentsResult.data || [])
        .map(mapGeneratedDocument)
        .filter((doc): doc is PortalDocument => doc !== null);

      // Generated documents of non-released rapporten (rapporten.zichtbaar =
      // false) are already filtered out by RLS, but SCIOS certificaten are
      // mined client-side from the (visible) rapport rows, so hide those here
      // until the rapport is released.
      let sciosRapporten = rapportenResult.data || [];
      try {
        const holds = await fetchRapportPaymentHolds(sciosRapporten.map((rapport: { id: string }) => rapport.id));
        sciosRapporten = sciosRapporten.filter((rapport: { id: string }) => holds.get(rapport.id)?.zichtbaar !== false);
      } catch (holdError) {
        console.error("Kon betaalblokkades niet laden:", holdError);
      }
      const scios = sciosRapporten.flatMap(mapSciosCertificaten);

      documenten.value = [...generated, ...scios].sort((left, right) => {
        return (right.date || "").localeCompare(left.date || "");
      });
    } catch (err) {
      console.error("Error fetching documenten:", err);
      captureError(err, { stage: "fetchDocumenten" });
      error.value = "Er is een fout opgetreden bij het ophalen van de documenten";
      toast.error(error.value, err);
    } finally {
      loading.value = false;
    }
  }

  async function downloadDocument(document: PortalDocument): Promise<void> {
    downloadingId.value = document.id;

    try {
      if (document.bucket === "userFiles") {
        await downloadSciosCertificaat(document.pdfPath, document.fileName);
        return;
      }

      const { data: signed, error: signError } = await supabase.storage
        .from("generated-docs")
        .createSignedUrl(document.pdfPath, 3600, { download: document.fileName });

      if (signError) throw signError;
      if (!signed?.signedUrl) throw new Error("Geen downloadlink beschikbaar");

      const link = window.document.createElement("a");
      link.href = signed.signedUrl;
      link.download = document.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading document:", err);
      captureError(err, { stage: "downloadDocument", documentId: document.id });
      // downloadSciosCertificaat surfaces its own toast; only toast for generated docs.
      if (document.bucket !== "userFiles") {
        toast.error("Het document kon niet gedownload worden. Probeer het later opnieuw.", err);
      }
    } finally {
      downloadingId.value = null;
    }
  }

  return {
    documenten,
    loading,
    error,
    downloadingId,
    fetchDocumenten,
    downloadDocument,
  };
}
